import { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '@platform/auth/frontend/contexts/AuthContext';
import { isAdminUser } from '@platform/app-shell/frontend/utils/adminAuth';
import { STORAGE_PREFIX } from '@client/config/client.config';
import { useLocation, useNavigate } from 'react-router-dom';
import { HOME_URL, HOME_TITLE } from '@platform/app-shell/frontend/utils/landing';
import { reorderTabs } from '@platform/app-shell/frontend/utils/tabOrder';

const TabContext = createContext(null);
const TabActionsContext = createContext(null);

// Identifies WHICH tab a page component is rendering inside. Provided by
// TabContentManager around every tab pane (App.jsx). Every tab stays
// mounted once opened — only the active one is displayed — so a page in a
// BACKGROUND tab keeps running effects and can call setTabTitle at any
// moment (a slow fetch resolving, a socket update landing). Without this
// context those calls landed on whatever tab happened to be active,
// renaming the foreground tab to a background tab's title: the
// "tab name doesn't match the screen under it" bug.
const TabPaneContext = createContext(null);

export function TabPaneProvider({ tabId, children }) {
  return <TabPaneContext.Provider value={tabId}>{children}</TabPaneContext.Provider>;
}

const STORAGE_KEY = `${STORAGE_PREFIX}_tabs`;
// Every open tab stays mounted (TabContentManager), so the cap is about
// memory, not room — the strip scrolls. At the cap a new tab is refused
// OUT LOUD (limitHits → TabBar's notice), never silently: it used to make
// the page under the current tab change with no tab to show for it.
export const MAX_TABS = 20;

function generateId() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
}

// Pathname portion of an internal URL ('/issues?jql=x' -> '/issues'). Used to
// match a programmatic navigation against the location change it produces.
function pathnameOf(url) {
  if (!url) return '';
  const q = url.indexOf('?');
  const h = url.indexOf('#');
  let end = url.length;
  if (q >= 0) end = Math.min(end, q);
  if (h >= 0) end = Math.min(end, h);
  return url.slice(0, end);
}

function deriveTitle(url) {
  // '/home' is HOME_URL (utils/landing.js), written out because this
  // function is also evaluated on its own by the grids suite.
  if (!url || url === '/' || url.split(/[?#]/)[0] === '/home') return 'Home';
  // A page that lists work items links to itself with ?item=KEY for one
  // work item's own screen (Grids & Gantts): the tab is that work item, so
  // it is named by its key, not by the page it was opened from.
  const itemKey = (url.match(/[?&]item=([^&#]+)/) || [])[1];
  if (itemKey) {
    try { return decodeURIComponent(itemKey); } catch { return itemKey; }
  }
  const pathOnly = url.split('?')[0];
  const parts = pathOnly.split('/').filter(Boolean);
  if (parts[0] === 'issues') return 'Work Item Results';
  if (parts[0] === 'structure') {
    return parts.length > 1 ? decodeURIComponent(parts[parts.length - 1]) : 'Folders';
  }
  if (parts[0] === 'zebra-simple-dashboards') {
    return parts.length > 1 ? decodeURIComponent(parts[parts.length - 1]) : 'Zebra Simple Dashboards';
  }
  if (parts[0] === 'settings') return 'Settings';
  if (parts[0] === 'profile') return 'My profile';
  if (parts[0] === 'field-option-editor') return 'Field Options';
  return parts[parts.length - 1] || 'Tab';
}

function loadFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const data = JSON.parse(raw);
      if (data.tabs && data.tabs.length > 0 && data.activeTabId) {
        return data;
      }
    }
  } catch (e) {
    // ignore
  }
  return null;
}

function saveToStorage(tabs, activeTabId) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ tabs, activeTabId }));
  } catch (e) {
    // ignore
  }
}

export function TabProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { userGroups } = useAuth();
  const isInitialMount = useRef(true);
  // Pathname of a navigation this provider issued itself (openTab / switchTab
  // / closeTab already wrote the tab's url, so the resulting location change
  // must not be re-recorded). Holds a PATHNAME rather than a boolean and is
  // consumed on the next location effect no matter what: a boolean flag went
  // stale whenever the navigation didn't change the pathname (opening a second
  // '/issues?jql=…' tab, say), and then swallowed the NEXT genuine navigation —
  // leaving the tab pointing at a page it no longer shows.
  const selfNavPathRef = useRef(null);

  // Captured at FIRST render, before any child effect can forward or
  // scrub the URL: a landing that carries auth artifacts (invite /
  // recovery tokens or GoTrue error fragments) must never be
  // tab-restored away. Needed because the initial-mount effect below
  // closes over the first-render router location — when Home forwards
  // '/' → '/auth/reset-password' synchronously before that effect runs,
  // the stale closure still reads '/', so a pathname check alone would
  // miss it and the restore would destroy the token.
  const landedWithAuthArtifactsRef = useRef(
    /[#&?](access_token|refresh_token|token_hash|type|error|error_code)=/
      .test((window.location.hash || '') + (window.location.search || ''))
  );

  // Global admin mode — persisted in sessionStorage so it survives refresh
  const [adminMode, setAdminMode] = useState(() => {
    try { return sessionStorage.getItem(`${STORAGE_PREFIX}_adminMode`) === 'true'; } catch { return false; }
  });
  const toggleAdminMode = useCallback(() => setAdminMode(prev => !prev), []);

  // Sync adminMode to sessionStorage
  useEffect(() => {
    try { sessionStorage.setItem(`${STORAGE_PREFIX}_adminMode`, String(adminMode)); } catch {}
  }, [adminMode]);

  // Turn off admin mode if the user is *proven* not to be admin —
  // i.e. their group list has actually loaded AND doesn't contain
  // ADMIN_GROUP. On a fresh page load (e.g. Ctrl+Shift+R while in
  // /settings), userGroups starts as null and only resolves once
  // /api/auth/me has fetched the group memberships. Without the
  // Array.isArray guard, this effect would fire BEFORE auth loads,
  // see adminMode=true + userGroups=null, conclude "not admin",
  // flip adminMode off, and AdminRoute would bounce the user out of
  // /settings before the auth call even completes.
  useEffect(() => {
    if (adminMode && Array.isArray(userGroups) && !isAdminUser(userGroups)) {
      setAdminMode(false);
    }
  }, [userGroups, adminMode]);

  // Ctrl+Space keyboard shortcut to toggle admin mode
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.code === 'Space' && isAdminUser(userGroups)) {
        e.preventDefault();
        setAdminMode(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [userGroups]);

  // Detect popout mode from ?popout=true query parameter
  const isPopout = useRef(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('popout') === 'true') {
      // Strip ?popout=true from URL without reload
      params.delete('popout');
      const clean = params.toString();
      const newUrl = window.location.pathname + (clean ? '?' + clean : '') + window.location.hash;
      window.history.replaceState(null, '', newUrl);
      return true;
    }
    return false;
  });
  // Evaluate once and store the boolean
  if (typeof isPopout.current === 'function') {
    isPopout.current = isPopout.current();
  }

  // A DEEP LINK the user explicitly opened (any pathname but '/') must win
  // over the restored tab set: without this, the initial-mount effect below
  // navigates to the restored active tab and silently swallows the link —
  // a shared /customs/... URL landed on whatever page was open last.
  // '/auth/*' landings are not tabs, '/mobile' lives outside the tab
  // system, and embedded windows already keep their URL (no restore).
  const deepLinkLanding = (() => {
    if (isPopout.current) return null;
    const p = location.pathname;
    if (p === '/' || p.startsWith('/auth/')) return null;
    // Home is where a sign-in lands, not a link someone followed: a person
    // with tabs open gets their tabs back (no Home tab added at each login).
    if (p === HOME_URL) return null;
    if (p === '/mobile' || p.startsWith('/mobile/')) return null;
    try {
      if (new URLSearchParams(window.location.search).get('embedded') === 'true') return null;
    } catch { /* unparseable search → treat as a plain landing */ }
    return p;
  })();

  const [tabs, setTabs] = useState(() => {
    // In popout mode, create a single fresh tab from the current pathname
    if (isPopout.current) {
      // Keep the query (already stripped of popout): docking the tab back
      // must carry e.g. a search's jql with it.
      const url = window.location.pathname + window.location.search;
      return [{ id: generateId(), url, title: deriveTitle(url) }];
    }
    const stored = loadFromStorage();
    if (stored) {
      // Adopt a deep link the restore would otherwise clobber: reuse its
      // existing tab (focused below), else append a tab for it.
      if (deepLinkLanding && !stored.tabs.some((t) => t.url === deepLinkLanding)) {
        return [...stored.tabs, { id: generateId(), url: deepLinkLanding, title: deriveTitle(deepLinkLanding) }];
      }
      return stored.tabs;
    }
    // Auth landings (invite / password-reset links) are not tabs — seed
    // the default instead so they can never be persisted and restored.
    const url = location.pathname.startsWith('/auth/') ? '/' : location.pathname;
    return [{ id: generateId(), url, title: deriveTitle(url) }];
  });

  const [activeTabId, setActiveTabId] = useState(() => {
    if (isPopout.current) return tabs[0].id;
    // The deep-link tab is the active one — its url equals the landing
    // pathname, so the initial-mount navigate below becomes a no-op and
    // the link's query string survives.
    if (deepLinkLanding) {
      const match = tabs.find((t) => t.url === deepLinkLanding);
      if (match) return match.id;
    }
    const stored = loadFromStorage();
    if (stored) return stored.activeTabId;
    return tabs[0].id;
  });

  // Refs for stable callbacks
  const activeTabIdRef = useRef(activeTabId);
  activeTabIdRef.current = activeTabId;
  const tabsRef = useRef(tabs);
  tabsRef.current = tabs;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const channelRef = useRef(null);

  // BroadcastChannel for cross-window tab docking
  useEffect(() => {
    let channel;
    try { channel = new BroadcastChannel(`${STORAGE_PREFIX}_tabs`); } catch (e) { return; }
    channelRef.current = channel;

    channel.onmessage = (e) => {
      // Only non-popout windows accept docked tabs
      if (e.data.type === 'dock-tab' && !isPopout.current) {
        const { tab } = e.data;
        const newTab = { id: generateId(), url: tab.url, title: tab.title };
        setTabs(prev => [...prev, newTab]);
        setActiveTabId(newTab.id);
        selfNavPathRef.current = pathnameOf(newTab.url);
        navigateRef.current(newTab.url);
        window.focus();
      }
    };

    return () => channel.close();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // On initial mount, navigate to the active tab's URL if different from current
  // Skip for standalone modules (e.g. /mobile) that don't use the tab system
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      if (location.pathname === '/mobile' || location.pathname.startsWith('/mobile/')) return;
      // Invite / password-reset landings must keep their URL — the hash/
      // query carries a one-time auth token, and navigate(activeTab.url)
      // stores pathname-only URLs, so a restore here would strand the
      // user on the sign-in page AND drop (or burn) the token. Checked
      // against the LIVE window.location (this effect's `location`
      // closure is the first-render one — stale when Home has already
      // forwarded '/' → '/auth/reset-password'), plus the render-time
      // auth-artifact capture above.
      if (window.location.pathname.startsWith('/auth/') || landedWithAuthArtifactsRef.current) return;
      if (new URLSearchParams(window.location.search).get('embedded') === 'true') return;
      const activeTab = tabs.find(t => t.id === activeTabId);
      if (activeTab && activeTab.url !== location.pathname) {
        navigate(activeTab.url, { replace: true });
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync location changes to active tab's URL (skip for standalone modules)
  useEffect(() => {
    const pathname = location.pathname;
    // One-shot: consumed here whether or not it matches, so a navigation that
    // left the pathname unchanged can't leave the guard armed for the next one.
    const selfNavPath = selfNavPathRef.current;
    selfNavPathRef.current = null;
    if (selfNavPath === pathname) return;
    if (pathname === '/mobile' || pathname.startsWith('/mobile/')) return;
    // Don't record auth landings — a persisted '/auth/reset-password'
    // tab would restore token-less on the NEXT app open and dead-end.
    if (pathname.startsWith('/auth/')) return;
    if (new URLSearchParams(window.location.search).get('embedded') === 'true') return;
    setTabs(prev => prev.map(tab =>
      tab.id === activeTabIdRef.current ? { ...tab, url: pathname } : tab
    ));
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist to localStorage (skip in popout mode and embedded mode)
  useEffect(() => {
    if (isPopout.current) return;
    if (new URLSearchParams(window.location.search).get('embedded') === 'true') return;
    saveToStorage(tabs, activeTabId);
  }, [tabs, activeTabId]);

  // Bumped each time a tab is refused at MAX_TABS; TabBar shows the notice.
  const [limitHits, setLimitHits] = useState(0);

  const openTab = useCallback((url, title) => {
    if (tabsRef.current.length >= MAX_TABS) { setLimitHits(n => n + 1); return; }
    const newId = generateId();
    const newTab = { id: newId, url, title: title || deriveTitle(url) };
    setTabs(prev => [...prev, newTab]);
    setActiveTabId(newId);
    selfNavPathRef.current = pathnameOf(url);
    navigateRef.current(url);
  }, []);

  const openBackgroundTab = useCallback((url, title) => {
    if (tabsRef.current.length >= MAX_TABS) { setLimitHits(n => n + 1); return; }
    setTabs(prev => {
      if (prev.length >= MAX_TABS) return prev;
      const newTab = { id: generateId(), url, title: title || deriveTitle(url) };
      const activeIndex = prev.findIndex(t => t.id === activeTabIdRef.current);
      const insertAt = activeIndex >= 0 ? activeIndex + 1 : prev.length;
      const next = [...prev];
      next.splice(insertAt, 0, newTab);
      return next;
    });
  }, []);

  const closeTab = useCallback((id) => {
    setTabs(prev => {
      if (prev.length === 1) {
        // In popout mode, close the window instead of creating a default tab
        if (isPopout.current) {
          window.close();
          return prev;
        }
        // The last tab closed leaves Home — the navigation as a page.
        const homeTab = { id: generateId(), url: HOME_URL, title: HOME_TITLE };
        setActiveTabId(homeTab.id);
        selfNavPathRef.current = HOME_URL;
        navigateRef.current(HOME_URL);
        return [homeTab];
      }
      const idx = prev.findIndex(t => t.id === id);
      const next = prev.filter(t => t.id !== id);
      if (id === activeTabIdRef.current) {
        const newIdx = Math.min(idx, next.length - 1);
        const newActive = next[newIdx];
        setActiveTabId(newActive.id);
        selfNavPathRef.current = pathnameOf(newActive.url);
        navigateRef.current(newActive.url);
      }
      return next;
    });
  }, []);

  // Turn tab `id` into another page in place — what Home does when a tile is
  // clicked, as a browser's new-tab page becomes the site you pick.
  const replaceTab = useCallback((id, url, title) => {
    setTabs(prev => prev.map(tab => (tab.id === id
      ? { ...tab, url, title: title || deriveTitle(url) }
      : tab)));
    setActiveTabId(id);
    selfNavPathRef.current = pathnameOf(url);
    navigateRef.current(url);
  }, []);

  // Bring Home to the front: its tab if one is open, else a new one.
  const openHome = useCallback(() => {
    const existing = tabsRef.current.find(t => pathnameOf(t.url) === HOME_URL);
    if (existing) {
      setActiveTabId(existing.id);
      selfNavPathRef.current = HOME_URL;
      navigateRef.current(existing.url);
      return;
    }
    openTab(HOME_URL, HOME_TITLE);
  }, [openTab]);

  // Drag a tab sideways (TabBar): `toIndex` is the slot it lands before.
  const moveTab = useCallback((id, toIndex) => {
    setTabs(prev => reorderTabs(prev, id, toIndex));
  }, []);

  const switchTab = useCallback((id) => {
    const tab = tabsRef.current.find(t => t.id === id);
    if (!tab) return;
    setActiveTabId(id);
    selfNavPathRef.current = pathnameOf(tab.url);
    navigateRef.current(tab.url);
  }, []);

  // `tabId` names the tab the calling page lives in. useSetTabTitle binds it
  // from TabPaneContext, so a background tab's late-resolving load renames
  // ITS OWN tab instead of whichever one the user is currently looking at.
  // Falls back to the active tab for callers rendered outside a pane
  // (embedded mode, popouts).
  const setTabTitle = useCallback((title, tabId) => {
    if (!title) return;
    const target = tabId || activeTabIdRef.current;
    setTabs(prev => {
      // Bail out on a no-op. Page effects re-fire this on every data change,
      // and returning a fresh array each time would re-render the whole tab
      // bar and rewrite localStorage for nothing.
      const current = prev.find(t => t.id === target);
      if (!current || current.title === title) return prev;
      return prev.map(tab => (tab.id === target ? { ...tab, title } : tab));
    });
  }, []);

  const popOutTab = useCallback((id, { screenX, screenY } = {}) => {
    const currentTabs = tabsRef.current;
    const tab = currentTabs.find(t => t.id === id);
    if (!tab || currentTabs.length <= 1) return;

    // The tab's url can already carry a query (a Zebra Search tab keeps its
    // ?jql=...): gluing on a second '?' buried the flag inside the jql, so
    // the new window never knew it was a popout — it restored EVERY tab and
    // ZQL choked on "DESC?popout=true".
    const target = new URL(tab.url, window.location.origin);
    target.searchParams.set('popout', 'true');
    const url = target.pathname + target.search + target.hash;
    const left = screenX != null ? screenX - 100 : window.screenX + 50;
    const top = screenY != null ? screenY - 50 : window.screenY + 50;
    window.open(
      url,
      '_blank',
      `width=1200,height=800,left=${left},top=${top}`
    );
    closeTab(id);
  }, [closeTab]);

  // Dock a tab back to the main window via BroadcastChannel
  const dockTab = useCallback((id) => {
    const tab = tabsRef.current.find(t => t.id === id);
    if (!tab) return;

    channelRef.current?.postMessage({
      type: 'dock-tab',
      tab: { url: tab.url, title: tab.title },
    });
    closeTab(id);
  }, [closeTab]);

  // Global middle-click interceptor (auxclick)
  useEffect(() => {
    const handleAuxClick = (e) => {
      if (e.button !== 1) return;
      const anchor = e.target.closest('a');
      if (!anchor) return;
      const href = anchor.getAttribute('href');
      if (!href || !href.startsWith('/') || href.startsWith('#')) return;
      e.preventDefault();
      e.stopPropagation();
      openBackgroundTab(href);
    };

    document.addEventListener('auxclick', handleAuxClick);
    return () => document.removeEventListener('auxclick', handleAuxClick);
  }, [openBackgroundTab]);

  // PWA launch handler — when an external link with the PWA's scope
  // is clicked (e.g. an email mention link) while this window is the
  // chosen target, the manifest's launch_handler focuses the existing
  // window and queues the target URL on window.launchQueue. We open
  // it as a new internal tab instead of replacing the user's current
  // view.
  //
  // The very first consumer call is the cold-launch URL itself, which
  // React Router + the tab bootstrap have already handled. Acting on
  // it here would race against Home's logged-in redirect to /structure
  // and add a stale '/' tab — so we always skip the first call,
  // regardless of whether its path matches the current location at the
  // time the consumer fires.
  useEffect(() => {
    if (!('launchQueue' in window)) return;
    let firstLaunchHandled = false;
    window.launchQueue.setConsumer((launchParams) => {
      if (!firstLaunchHandled) {
        firstLaunchHandled = true;
        return;
      }
      const target = launchParams && launchParams.targetURL;
      if (!target) return;
      let path;
      try {
        const u = new URL(target, window.location.origin);
        path = u.pathname + u.search + u.hash;
      } catch {
        return;
      }
      openTab(path);
    });
  }, [openTab]);

  // Global Ctrl+click interceptor (capture phase)
  useEffect(() => {
    const handleClick = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      if (e.button !== 0) return;
      const anchor = e.target.closest('a');
      if (!anchor) return;
      const href = anchor.getAttribute('href');
      if (!href || !href.startsWith('/') || href.startsWith('#')) return;
      e.preventDefault();
      e.stopPropagation();
      openBackgroundTab(href);
    };

    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [openBackgroundTab]);

  const value = {
    tabs,
    activeTabId,
    isPopoutWindow: isPopout.current,
    openTab,
    openBackgroundTab,
    closeTab,
    replaceTab,
    openHome,
    switchTab,
    moveTab,
    limitHits,
    setTabTitle,
    popOutTab,
    dockTab,
    adminMode,
    toggleAdminMode,
  };

  // Stable actions context — never changes, so page components won't re-render
  const actionsValue = useMemo(() => ({ setTabTitle }), [setTabTitle]);

  return (
    <TabContext.Provider value={value}>
      <TabActionsContext.Provider value={actionsValue}>
        {children}
      </TabActionsContext.Provider>
    </TabContext.Provider>
  );
}

// For TabBar and TabContentManager — subscribes to tab state changes
export function useTab() {
  const ctx = useContext(TabContext);
  if (!ctx) throw new Error('useTab must be used within a TabProvider');
  return ctx;
}

// The same, or null outside a TabProvider (a popout, an embedded page) —
// for a component drawn in both, which then falls back to its href.
export function useOptionalTab() {
  return useContext(TabContext);
}

// Is the tab this component renders inside the one in front? Every tab
// stays mounted, so anything a page PORTALS to <body> (a dialog, a sheet)
// outlives a tab switch and covers the tab the reader just went to — "View
// Workflow" opened its tab underneath the work item's pop-up. Such a layer
// asks this and draws nothing while its tab is behind. True outside a tab
// pane (popouts, embedded pages, labs) — there is nothing to be behind.
export function useIsPaneActive() {
  const ctx = useContext(TabContext);
  const paneTabId = useContext(TabPaneContext);
  if (!ctx || !paneTabId) return true;
  return ctx.activeTabId === paneTabId;
}

// The id of the tab this component renders inside (null outside a pane —
// popouts, embedded pages). For a page that turns its own tab into another
// page (Home → replaceTab).
export function useTabPaneId() {
  return useContext(TabPaneContext);
}

// For page components — stable, never triggers re-renders on tab switch.
// The returned setter is bound to the tab this component renders inside
// (TabPaneContext), so it can only ever rename its own tab.
export function useSetTabTitle() {
  const ctx = useContext(TabActionsContext);
  if (!ctx) throw new Error('useSetTabTitle must be used within a TabProvider');
  const paneTabId = useContext(TabPaneContext);
  return useMemo(
    () => (title) => ctx.setTabTitle(title, paneTabId),
    [ctx, paneTabId],
  );
}
